"use client"

import { FC, useEffect, useState } from "react"
import { Star, ExternalLink } from "lucide-react"
import { useContent } from "@/src/context/ContentContext"
import { useLanguage } from "@/src/context/LanguageContext"
import { useUI } from "@/src/lib/i18n/ui"

interface GoogleStats {
  rating: number
  userRatingCount: number
}

const GOOGLE_REVIEW_URL =
  "https://search.google.com/local/writereview?placeid=ChIJRXvX0vUREycRtsJpMGXs5RI"
const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/7cSHR7VYrYXi1gB49"

function GoogleLogo({ className = "w-6 h-6" }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className}>
      <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  )
}

function Stars({ rating, size = "md" }: { rating: number; size?: "sm" | "md" | "lg" }) {
  const sizes = { sm: "w-4 h-4", md: "w-5 h-5", lg: "w-7 h-7" }
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={sizes[size]}
          fill={s <= rating ? "#FACC15" : "transparent"}
          stroke={s <= rating ? "#FACC15" : "#4B5563"}
        />
      ))}
    </div>
  )
}

const Reviews: FC = () => {
  const { reviews } = useContent()
  const { lang } = useLanguage()
  const t = useUI(lang)
  const [stats, setStats] = useState<GoogleStats | null>(null)

  useEffect(() => {
    fetch("/api/reviews")
      .then((r) => r.json())
      .then(setStats)
      .catch(() => setStats({ rating: 5, userRatingCount: 0 }))
  }, [])

  const rating = stats?.rating ?? 5
  const count = stats?.userRatingCount ?? 0
  const visibleReviews = reviews.filter((r) => r.text.trim().length > 0)

  return (
    <section className="py-24 bg-gradient-to-b from-[#111216] via-[#0d0e11] to-[#111216]">
      <div className="container mx-auto">

        {/* Section label + title */}
        <div className="mb-12">
          <p className="text-gray-500 text-sm uppercase tracking-widest mb-3">{t("reviewsLabel")}</p>
          <h2 className="text-3xl md:text-4xl font-semibold text-white">
            {t("reviewsTitle")}
          </h2>
        </div>

        {/* Google rating hero card */}
        <a
          href={GOOGLE_MAPS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group block mb-12"
        >
          <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] hover:border-white/20 transition-all duration-300">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-32 bg-yellow-400/10 blur-3xl pointer-events-none" />

            <div className="relative flex flex-col md:flex-row items-center gap-8 md:gap-12 px-8 md:px-14 py-10 md:py-12">

              {/* Google logo + label */}
              <div className="flex flex-col items-center gap-2 flex-shrink-0">
                <GoogleLogo className="w-10 h-10" />
                <span className="text-gray-400 text-xs font-medium tracking-wide">Google Reviews</span>
              </div>

              {/* Divider */}
              <div className="hidden md:block w-px h-20 bg-white/10" />

              {/* Big rating */}
              <div className="flex flex-col items-center md:items-start gap-2">
                <div className="flex items-end gap-3">
                  <span className="text-7xl md:text-8xl font-bold text-white leading-none tracking-tight">
                    {rating.toFixed(1)}
                  </span>
                  <span className="text-gray-500 text-lg mb-2">/ 5</span>
                </div>
                <Stars rating={Math.round(rating)} size="lg" />
              </div>

              {/* Divider */}
              <div className="hidden md:block w-px h-20 bg-white/10" />

              {/* Count + cta hint */}
              <div className="flex flex-col items-center md:items-start gap-1">
                <span className="text-4xl md:text-5xl font-semibold text-white">
                  {count > 0 ? count : "5★"}
                </span>
                <span className="text-gray-400 text-sm">
                  {count > 0 ? t("reviewsCountLabel") : t("reviewsRatingLabel")}
                </span>
                <span className="mt-3 text-xs text-gray-600 group-hover:text-gray-400 transition-colors flex items-center gap-1">
                  {t("reviewsSeeOnGoogle")} <ExternalLink className="w-3 h-3" />
                </span>
              </div>

            </div>
          </div>
        </a>

        {/* Review cards */}
        {visibleReviews.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {visibleReviews.map((review) => (
              <div
                key={review.id}
                className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 flex flex-col gap-4 hover:bg-white/[0.07] hover:border-white/20 transition-all duration-200"
              >
                <Stars rating={review.rating} size="sm" />
                <p className="text-gray-300 text-sm leading-relaxed flex-1">
                  "{review.text}"
                </p>
                <div className="flex items-center justify-between pt-2 border-t border-white/[0.06]">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                      {review.author.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm leading-tight">{review.author}</p>
                      <p className="text-gray-500 text-xs">{review.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <GoogleLogo className="w-4 h-4" />
                    <span className="text-gray-600 text-xs">Google</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white text-black font-medium px-8 py-3.5 rounded-full hover:bg-gray-100 transition-colors text-sm"
          >
            {t("reviewsCtaWrite")}
            <ExternalLink className="w-4 h-4" />
          </a>
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 border border-white/20 text-gray-300 font-medium px-8 py-3.5 rounded-full hover:border-white/40 hover:text-white transition-colors text-sm"
          >
            {t("reviewsCtaAll")}
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

      </div>
    </section>
  )
}

export default Reviews
